export interface Skill {
  id: string;
  name: string;
  description: string;
  category: string;
  skill_type: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
  is_favourite?: boolean;
  instruction?: string;
  skill_shortcut?: string;
}

export interface Pagination {
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_more: boolean;
}

export interface SkillListData {
  group: string;
  skills: Skill[];
  pagination: Pagination;
}

export interface SkillListResponse {
  status: string;
  message: string;
  data: SkillListData;
}

export interface SkillGroupState {
  skills: Skill[];
  pagination: Pagination;
  isLoading: boolean;
  error: string | null;
}

interface SkillState {
  skillGroups: Record<string, SkillGroupState>;
  setSkillGroupData: (key: string, response: SkillListResponse) => void;
  setSkillFavourite: (skillId: string, isFavourite: boolean) => void;
  getSkillById: (skillId: string) => Skill | undefined;
}

export const DEFAULT_SKILL_PAGE_SIZE = 20;

const initialSkillState: SkillState = {
  skillGroups: {},
  setSkillGroupData: () => {},
  setSkillFavourite: () => {},
  getSkillById: () => undefined,
};

export const skillSlice = (set, get): SkillState => ({
  ...initialSkillState,

  setSkillGroupData: (key: string, { data }: SkillListResponse) => {
    set((state) => {
      const existing = state.skillGroups[key];
      // Page 1 is a fresh load; otherwise append, skipping ids we already hold
      const skills =
        data.pagination.page <= 1
          ? data.skills
          : [
              ...existing.skills,
              ...data.skills.filter((s) => !existing.skills.some((e) => e.id === s.id)),
            ];

      return {
        ...state,
        skillGroups: {
          ...state.skillGroups,
          [key]: {
            skills,
            pagination: data.pagination,
            isLoading: false,
            error: null,
          },
        },
      };
    });
  },

  setSkillFavourite: (skillId: string, isFavourite: boolean) => {
    set((state) => {
      const skillGroups = Object.fromEntries(
        Object.entries(state.skillGroups as Record<string, SkillGroupState>).map(([key, group]) => [
          key,
          {
            ...group,
            skills: group.skills.map((s) =>
              s.id === skillId ? { ...s, is_favourite: isFavourite } : s
            ),
          },
        ])
      );
      return { ...state, skillGroups };
    });
  },
  getSkillById: (skillId: string) => {
    const groups: SkillGroupState[] = Object.values(get().skillGroups);
    for (const group of groups) {
      const found = group.skills.find((s) => s.id === skillId);
      if (found) return found;
    }
    return undefined;
  },
});
